import {
  YStack,
  useDidFinishSSR,
  Dialog,
  isWeb,
  DialogProps,
  Stack,
  XStack,
} from '@mythical/ui'
import { Button } from '../primitive/Button'
import { SolitoImage } from 'solito/image'
import { Confetti } from '../primitive/Confetti'
import { IconUnlocked } from '../primitive/Icon'

/**
 * Celebration Modal shown after successful upgrade.
 * Always shown after upgrade (all platforms, all regions - informational only).
 * Only shown once per upgrade (persisted via store).
 */
export const CelebrationModal = ({ open, ...props }: DialogProps) => {
  const didSSR = useDidFinishSSR()

  if (!didSSR) return null

  return (
    <Dialog modal open={open} {...props}>
      {/* On web mobile and native always use sheet. Dialogs broken on native */}
      <Dialog.Adapt {...(isWeb ? { when: 'xs' } : { platform: 'touch' })}>
        <Dialog.Sheet
          modal
          dismissOnSnapToBottom
          animation="medium"
          snapPointsMode="fit"
        >
          <Dialog.Sheet.Overlay
            bg="$backdrop"
            animation="quick"
            enterStyle={{ o: 0 }}
            exitStyle={{ o: 0 }}
          />

          {open && !isWeb && (
            <Stack
              pos="absolute"
              w="100%"
              h="100%"
              zIndex={1000}
              pointerEvents="none"
            >
              <Confetti duration={5000} delay={1000} />
            </Stack>
          )}

          <Dialog.Sheet.Frame theme="pink" pt="$2" pb="$5">
            <Dialog.Sheet.Handle bg="$fgPrimary_op60" />
            <Dialog.Adapt.Contents />
          </Dialog.Sheet.Frame>
        </Dialog.Sheet>
      </Dialog.Adapt>

      <Dialog.Portal>
        <Dialog.Overlay
          key={`overlay`}
          bg="$backdrop"
          animateOnly={['transform', 'opacity']}
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />

        <Dialog.Content
          key="content"
          pos={isWeb ? ('static' as 'relative') : 'relative'}
          theme="pink"
          br="$8"
          p="$0"
          overflow="visible"
          maxWidth={720}
          w={isWeb ? 'calc(100% - 32px)' : '100%'}
          elevate
          enterStyle={{ x: 0, y: -20, opacity: 0 }}
          exitStyle={{ x: 0, y: 10, opacity: 0 }}
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
        >
          <YStack p="$3.5" gap="$6">
            <YStack w="100%" maw={300} mx="auto">
              <Stack aspectRatio={1}>
                <SolitoImage
                  src={require('app/assets/images/all-access-unlocked.png')}
                  fill
                  contentFit="contain"
                  alt="Cockatrice plush"
                  sizes="300px"
                />
              </Stack>
            </YStack>

            <YStack ai="center" gap="$3.5">
              <XStack gap="$2" ai="center" jc="center">
                <IconUnlocked
                  size={64}
                  fs={0}
                  color="$fgBrand"
                  $platform-native={{ bottom: 6 }}
                />
                <Dialog.Title
                  ff="$display"
                  size="$l"
                  color="$fgBrand"
                  tt="uppercase"
                  fs={1}
                  fg={0}
                  maxWidth={275}
                >
                  All Access Unlocked!
                </Dialog.Title>
              </XStack>
              <Dialog.Description
                color="$fgBrand"
                size="$2"
                fow="500"
                ta="center"
                maw={275}
              >
                You’re officially All Access. Collectibles, exclusives, and more
                await.
              </Dialog.Description>
            </YStack>

            <Button
              mt="auto"
              theme="primary"
              onPress={() => props.onOpenChange?.(false)}
              size="$5"
            >
              Lets Go!
            </Button>
          </YStack>
          {open && isWeb && (
            <Stack
              pos="absolute"
              top={0}
              left={0}
              w="100%"
              h="100%"
              zIndex={1000}
              pointerEvents="none"
            >
              <Confetti duration={5000} delay={250} />
            </Stack>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  )
}
